import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { LAB_GATE_LABELS, type ImagingExamView } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, Section, Tag, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { imagingExamQuery, verifyImagingReport } from '../api/imaging.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, ShortcutHint } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { heldBySha, ShaApprovalTag } from '../sha/approval-tag.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { ImagesLinkDialog, PerformDialog, ScheduleDialog } from './exam-dialogs.tsx';
import { modalityWords, STATE_SHAPES, stateWords } from './format.ts';
import styles from './imaging.module.css';
import { PrintImagingReportDialog } from './prints.tsx';
import { ReportEditor } from './report-editor.tsx';
import { ImagingReportBody, ReportStatusTag } from './report-view.tsx';

// One imaging exam (docs/plans/phase2-radiology.md §6): the patient banner, the request,
// then what can be done next by who is signed in: schedule, mark performed, write, verify,
// amend and print the report. Keys S, P and R act when the action is on screen.

const route = getRouteApi('/_app/imaging/exams/$orderId');

type Open = 'schedule' | 'perform' | 'link' | 'print' | null;

function RequestFacts({ exam }: { exam: ImagingExamView }) {
  return (
    <dl className={styles.facts}>
      <dt>Exam</dt>
      <dd>
        <span className="wodi-id">{exam.itemCode}</span> {exam.itemName}
      </dd>
      <dt>Modality</dt>
      <dd>
        {modalityWords(exam.modality)}
        {exam.bodyPart === null ? '' : `, ${exam.bodyPart}`}
      </dd>
      <dt>Requested</dt>
      <dd>{`${formatInstant(exam.orderedAt)} by ${exam.orderingUser.displayName}`}</dd>
      <dt>Priority</dt>
      <dd>{exam.priority === 'urgent' ? <Tag state="active">Urgent</Tag> : 'Routine'}</dd>
      <dt>Clinical question</dt>
      <dd className={styles.prose}>{exam.reason ?? 'None given'}</dd>
      {exam.statusReason === null ? null : (
        <>
          <dt>Why</dt>
          <dd>{exam.statusReason}</dd>
        </>
      )}
    </dl>
  );
}

function ExamFacts({
  exam,
  mayLink,
  onLink,
}: {
  exam: ImagingExamView;
  mayLink: boolean;
  onLink: () => void;
}) {
  return (
    <dl className={styles.facts}>
      <dt>Scheduled</dt>
      <dd>
        {exam.scheduledFor === null
          ? 'Not scheduled'
          : `${formatInstant(exam.scheduledFor)}${exam.scheduleNote === null ? '' : `, ${exam.scheduleNote}`}`}
      </dd>
      <dt>Performed</dt>
      <dd>
        {exam.performedAt === null
          ? 'Not yet'
          : `${formatInstant(exam.performedAt)} by ${exam.performedBy?.displayName ?? ''}`}
      </dd>
      {exam.performedNote === null ? null : (
        <>
          <dt>Radiographer's note</dt>
          <dd className={styles.prose}>{exam.performedNote}</dd>
        </>
      )}
      {exam.performedAt === null ? null : (
        <>
          <dt>Images</dt>
          <dd className={styles.actions}>
            {exam.imagesLink === null ? (
              <span>No link to the images</span>
            ) : (
              <a href={exam.imagesLink} target="_blank" rel="noopener noreferrer">
                Open images
              </a>
            )}
            {mayLink ? (
              <Button dense variant="quiet" onPress={onLink}>
                {exam.imagesLink === null ? 'Add link' : 'Change link'}
              </Button>
            ) : null}
          </dd>
        </>
      )}
    </dl>
  );
}

export function ImagingExamPage() {
  const { orderId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const exam = useQuery(imagingExamQuery(orderId));
  const online = useOnline();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<Open>(null);
  const [editing, setEditing] = useState<'write' | 'amend' | null>(null);
  const may = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);

  const verify = useMutation({
    mutationFn: (e: ImagingExamView) =>
      verifyImagingReport(e.report?.id ?? '', e.report?.currentVersion ?? 0),
    onSuccess: async (next) => {
      queryClient.setQueryData(['imaging', 'exam', orderId], next);
      await queryClient.invalidateQueries({ queryKey: ['imaging', 'worklist'] });
      toasts.show({ title: 'Report verified' });
    },
  });

  const e = exam.data;
  const waiting = e !== undefined && (e.state === 'requested' || e.state === 'scheduled');
  const canSchedule = waiting && may('imaging.schedule') && online;
  const canPerform = waiting && may('imaging.perform') && online && e.gate === 'ready';
  const canWrite =
    e?.state === 'performed' && may('imaging.report.write') && online && editing === null;
  useShortcut(
    's',
    outsideDialogs(() => {
      if (canSchedule) setOpen('schedule');
    }),
    { description: 'Schedule exam' },
  );
  useShortcut(
    'p',
    outsideDialogs(() => {
      if (canPerform) setOpen('perform');
    }),
    { description: 'Mark performed' },
  );
  useShortcut(
    'r',
    outsideDialogs(() => {
      if (canWrite) setEditing('write');
    }),
    { description: 'Write report' },
  );

  if (session === null || !can(session, 'imaging.read')) {
    return (
      <>
        <PageHeader title="Imaging exam" />
        <Notice tone="info">
          You don't have permission to see imaging exams. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  if (exam.isPending) return <LoadingBlock label="Loading the exam" rows={6} />;
  if (exam.isError || e === undefined) {
    return (
      <Notice tone="error" action={<Button onPress={() => void exam.refetch()}>Try again</Button>}>
        {errorText(exam.error, "The exam couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const report = e.report;
  const reported = report !== null && e.state !== 'entered_in_error';
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={e.patient.id} />
      <PageHeader
        title={e.itemName}
        description={stateWords(e.state)}
        actions={
          <>
            {waiting && may('imaging.schedule') ? (
              <Button
                shortcut="s"
                variant={e.state === 'requested' ? 'primary' : 'secondary'}
                isDisabled={!online}
                onPress={() => {
                  setOpen('schedule');
                }}
              >
                {e.state === 'scheduled' ? 'Change time' : 'Schedule exam'}
              </Button>
            ) : null}
            {waiting && may('imaging.perform') ? (
              <Button
                shortcut="p"
                variant={e.state === 'scheduled' ? 'primary' : 'secondary'}
                isDisabled={!canPerform}
                onPress={() => {
                  setOpen('perform');
                }}
              >
                Mark performed
              </Button>
            ) : null}
            {e.state === 'performed' && may('imaging.report.write') && editing === null ? (
              <Button
                shortcut="r"
                variant="primary"
                isDisabled={!online}
                onPress={() => {
                  setEditing('write');
                }}
              >
                Write report
              </Button>
            ) : null}
            {e.state === 'reported' && may('imaging.report.verify') && editing === null ? (
              <Button
                variant="primary"
                isDisabled={!online}
                isPending={verify.isPending}
                pendingLabel="Verifying…"
                onPress={() => {
                  verify.mutate(e);
                }}
              >
                Verify report
              </Button>
            ) : null}
            {reported && may('imaging.report.write') && editing === null ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setEditing('amend');
                }}
              >
                Amend report
              </Button>
            ) : null}
            {reported && may('imaging.report.print') ? (
              <Button
                variant="quiet"
                onPress={() => {
                  setOpen('print');
                }}
              >
                Print report
              </Button>
            ) : null}
          </>
        }
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Scheduling, performing and reporting wait for the connection.
        </Notice>
      ) : null}
      {waiting ? (
        <ShortcutHint combo="s" action="schedule, or P to mark the exam performed" />
      ) : null}
      {verify.isError ? (
        <Notice tone="error">
          {errorText(verify.error, "The report couldn't be verified. Reload and try again.")}
        </Notice>
      ) : null}
      <Section
        title="Request"
        actions={<Tag state={STATE_SHAPES[e.state]}>{stateWords(e.state)}</Tag>}
      >
        <RequestFacts exam={e} />
      </Section>
      {waiting ? (
        <Section title="Before the exam">
          <div className={styles.actions}>
            {heldBySha(e.shaApproval) ? null : (
              <span>{e.gate === 'ready' ? 'Ready to perform' : LAB_GATE_LABELS[e.gate]}</span>
            )}
            <ShaApprovalTag approval={e.shaApproval} />
          </div>
          <p className={styles.prose}>
            {e.preparation === null
              ? 'No preparation instructions set for this exam in Settings > Imaging.'
              : `Preparation: ${e.preparation}`}
          </p>
        </Section>
      ) : null}
      <Section title="Exam">
        <ExamFacts
          exam={e}
          mayLink={may('imaging.perform') && online && e.state !== 'entered_in_error'}
          onLink={() => {
            setOpen('link');
          }}
        />
      </Section>
      {editing !== null ? (
        <Section title={editing === 'amend' ? 'Amend the report' : 'Write the report'}>
          <ReportEditor
            exam={e}
            mode={editing}
            mayVerify={may('imaging.report.verify')}
            onDone={() => {
              setEditing(null);
            }}
          />
        </Section>
      ) : null}
      {report === null ? (
        e.state === 'performed' && editing === null ? (
          <Section title="Report">
            <p className={styles.meta}>Not reported yet.</p>
          </Section>
        ) : null
      ) : (
        <Section title="Report" actions={<ReportStatusTag report={report} />}>
          <ImagingReportBody report={report} />
        </Section>
      )}
      <p className={styles.meta}>
        <Link to="/imaging">Back to the imaging worklist</Link>
      </p>
      {open === 'schedule' ? (
        <ScheduleDialog
          exam={e}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'perform' ? (
        <PerformDialog
          exam={e}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'link' ? (
        <ImagesLinkDialog
          exam={e}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'print' && report !== null ? (
        <PrintImagingReportDialog
          data={{
            facilityName: session.facility.name,
            patient: {
              displayName: e.patient.displayName,
              patientNumber: e.patient.patientNumber,
              sex: e.patient.sex.display,
              age: e.patient.age,
            },
            report,
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </div>
  );
}
